import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Banking.css';

const TABS = ['Banks', 'Bank Statement', 'Profit & Loss', 'Balance Sheet'];
const TAB_KEYS = {
  'Banks': 'banks',
  'Bank Statement': 'bankStatement',
  'Profit & Loss': 'profitAndLoss',
  'Balance Sheet': 'balanceSheet',
};

export default function Banking() {
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState('Banks');
  return (
    <div className="banking-page">
      <div className="page-header"><h2 className="page-title">{tr('banking.title')}</h2></div>

      <div className="bk-tabs">
        {TABS.map(t => (
          <button key={t} className={`bk-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{tr('banking.tabs.' + TAB_KEYS[t])}</button>
        ))}
      </div>

      <div className="bk-body">
        {tab === 'Banks'          && <BanksTab />}
        {tab === 'Bank Statement' && <StatementTab />}
        {tab === 'Profit & Loss'  && <PnLTab />}
        {tab === 'Balance Sheet'  && <BalanceSheetTab />}
      </div>
    </div>
  );
}

const inr = (n) => '₹ ' + fmtNum(n);

/* ═══════════════════════════════════════════════════════════
   1. BANKS — list, register, edit, set default
═══════════════════════════════════════════════════════════ */
const EMPTY_BANK = {
  name: '', bank_name: '', account_no: '', ifsc: '', branch: '',
  account_type: 'Current', opening_balance: '', opening_date: today(),
  is_default: false,
};

function BanksTab() {
  const { t: tr } = useTranslation();
  const { data, loading, error, reload } = useApi(
    () => api.banks.list(), [],
    { banks: [], total_balance: 0, total_in: 0, total_out: 0, count: 0 }
  );
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const startEdit = (b) => {
    setForm({
      name: b.name || '', bank_name: b.bank_name || '',
      account_no: b.account_no || '', ifsc: b.ifsc || '',
      branch: b.branch || '', account_type: b.account_type || 'Current',
      opening_balance: b.opening_balance || 0,
      opening_date: b.opening_date || today(),
      is_default: !!b.is_default,
    });
    setEditId(b.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('❌ ' + tr('banking.errors.bankNameRequired')); return; }
    setSaving(true);
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance || 0) };
      if (editId) await api.banks.update(editId, payload);
      else        await api.banks.create(payload);
      notify(editId ? '✅ ' + tr('banking.toasts.bankUpdated') : '✅ ' + tr('banking.toasts.bankRegistered'));
      setShowForm(false); setForm(EMPTY_BANK); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const setDefault = async (id) => {
    try { await api.banks.setDefault(id); notify('✅ ' + tr('banking.toasts.defaultBankSet')); reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };

  const handleDelete = async () => {
    try { await api.banks.remove(confirm.id); notify('✅ ' + tr('banking.toasts.deleted')); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="bk-section">
      {msg && <div className="bk-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="bk-kpis">
          <Kpi label={tr('banking.kpis.totalBalance')} value={inr(data.total_balance)} tone="green" />
          <Kpi label={tr('banking.kpis.totalMoneyIn')} value={inr(data.total_in)} tone="blue" />
          <Kpi label={tr('banking.kpis.totalMoneyOut')} value={inr(data.total_out)} tone="orange" />
          <Kpi label={tr('banking.kpis.activeBanks')} value={data.count} />
        </div>
      )}

      <div className="bk-actions">
        <button className="btn btn-primary btn-sm"
                onClick={() => { setShowForm(!showForm); setForm(EMPTY_BANK); setEditId(null); }}>
          {showForm ? '✕ ' + tr('common.cancel') : '＋ ' + tr('banking.registerBank')}
        </button>
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? tr('banking.editBank') : tr('banking.registerNewBank')}</div>
          <div className="bk-form-grid">
            <div className="form-group"><label>{tr('banking.displayName')} *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="HDFC Current" /></div>
            <div className="form-group"><label>{tr('banking.bankName')}</label>
              <input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="HDFC Bank Ltd" /></div>
            <div className="form-group"><label>{tr('banking.accountNo')}</label>
              <input value={form.account_no} onChange={e => setForm({ ...form, account_no: e.target.value })} /></div>
            <div className="form-group"><label>IFSC</label>
              <input value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
            <div className="form-group"><label>{tr('banking.branch')}</label>
              <input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} /></div>
            <div className="form-group"><label>{tr('banking.accountType')}</label>
              <select value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}>
                <option>Current</option><option>Savings</option><option>OD</option><option>CC</option><option>Cash</option>
              </select></div>
            <div className="form-group"><label>{tr('banking.openingBalance')}</label>
              <input type="number" step="0.01" value={form.opening_balance}
                     onChange={e => setForm({ ...form, opening_balance: e.target.value })} /></div>
            <div className="form-group"><label>{tr('banking.openingDate')}</label>
              <input type="date" value={form.opening_date}
                     onChange={e => setForm({ ...form, opening_date: e.target.value })} /></div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_default}
                       onChange={e => setForm({ ...form, is_default: e.target.checked })} />
                {tr('banking.setAsDefaultBank')}
              </label></div>
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ ' + tr('common.saving') : (editId ? tr('common.update') : tr('banking.register'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{tr('banking.registeredBanks')}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('banking.account')}</th>
                <th style={{ textAlign: 'left' }}>{tr('banking.ifscBranch')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.type')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.opening')}</th>
                <th style={{ textAlign: 'right' }}>{tr('banking.in')}</th>
                <th style={{ textAlign: 'right' }}>{tr('banking.out')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.balance')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.banks.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {tr('banking.emptyBanks')}
                </td></tr>
              ) : data.banks.map(b => (
                <tr key={b.id}>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>
                    {b.name}
                    {Number(b.is_default) === 1 && <span className="pill-default">{tr('banking.default')}</span>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{b.bank_name}</div>
                  </td>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace' }}>{b.account_no || '—'}</td>
                  <td style={{ textAlign: 'left' }}>
                    <div>{b.ifsc || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.branch || ''}</div>
                  </td>
                   <td style={{ textAlign: 'left' }}>{b.account_type}</td>
                  <td style={{ textAlign: 'right' }}>{inr(b.opening_balance)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{inr(b.total_in)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{inr(b.total_out)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(b.current_balance)}</td>
                  <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" onClick={() => startEdit(b)}>✏️</button>
                    {Number(b.is_default) !== 1 && (
                      <button className="btn btn-sm" onClick={() => setDefault(b.id)} title={tr('banking.setAsDefault')}>⭐</button>
                    )}
                    <button className="btn btn-sm"
                            style={{ color: 'var(--danger)' }}
                            onClick={() => setConfirm(b)} title={tr('common.delete')}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={tr('banking.deleteBankTitle')}
        message={confirm ? tr('banking.deleteBankMsg', { name: confirm.name }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={tr('common.delete')}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   2. BANK STATEMENT — per-bank running balance
═══════════════════════════════════════════════════════════ */
function StatementTab() {
  const { t: tr } = useTranslation();
  const [bankId, setBankId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());

  React.useEffect(() => {
    const d = new Date();
    const prev = new Date(d.getTime() - 30 * 86400 * 1000);
    setFrom(prev.toISOString().slice(0, 10));
  }, []);

  const { data: banks } = useApi(() => api.banks.list(), [], { banks: [] });
  const { data, loading, error, reload } = useApi(
    () => bankId && from && to ? api.banks.statement(bankId, from, to)
                                : Promise.resolve({ rows: [], opening_balance: 0, closing_balance: 0, total_in: 0, total_out: 0, bank: {} }),
    [bankId, from, to],
    { rows: [], opening_balance: 0, closing_balance: 0, total_in: 0, total_out: 0, bank: {} }
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>{tr('common.bank')}</label>
          <select value={bankId} onChange={e => setBankId(e.target.value)}>
            <option value="">{tr('banking.selectBank')}</option>
            {(banks.banks || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></div>
        <div className="form-group"><label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload} disabled={!bankId}>{tr('banking.load')}</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {!bankId ? (
        <div className="bk-hint">👆 {tr('banking.selectBankHint')}</div>
      ) : loading ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label={tr('banking.openingBalance')} value={inr(data.opening_balance)} />
            <Kpi label={tr('banking.kpis.totalIn')}   value={inr(data.total_in)}        tone="green" />
            <Kpi label={tr('banking.kpis.totalOut')}  value={inr(data.total_out)}       tone="orange" />
            <Kpi label={tr('banking.closingBalance')} value={inr(data.closing_balance)} tone="blue" />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">
              {data.bank?.name} · {data.bank?.account_no || '—'} · {from} to {to}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('banking.source')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('banking.party')}</th>
                   <th style={{ textAlign: 'left' }}>{tr('banking.mode')}</th>
                  <th style={{ textAlign: 'left' }}>{tr('banking.ref')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('banking.creditIn')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('banking.debitOut')}</th>
                  <th style={{ textAlign: 'right' }}>{tr('common.balance')}</th>
                </tr>
              </thead>
              <tbody>
                 <tr style={{ background: '#f5f9fc', fontWeight: 600 }}>
                  <td colSpan="7" style={{ textAlign: 'right' }}>{tr('banking.openingBalance')}:</td>
                  <td style={{ textAlign: 'right' }}>{inr(data.opening_balance)}</td>
                </tr>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{tr('banking.noTransactions')}</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{r.entry_date}</td>
                    <td style={{ textAlign: 'left' }}>{r.reference_type}</td>
                     <td style={{ textAlign: 'left' }}>{r.party_name || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.mode || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.reference_no || '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{r.type === 'Debit'  ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{r.type === 'Credit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(r.running_balance)}</td>
                  </tr>
                ))}
                 <tr style={{ background: '#f5f9fc', fontWeight: 700 }}>
                  <td colSpan="5" style={{ textAlign: 'right' }}>{tr('banking.totals')}:</td>
                  <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{inr(data.total_in)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{inr(data.total_out)}</td>
                  <td style={{ textAlign: 'right' }}>{inr(data.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   3. PROFIT & LOSS
═══════════════════════════════════════════════════════════ */
function PnLTab() {
  const { t: tr } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState(today());

  React.useEffect(() => {
    const d = new Date();
    setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.financials.pnl(from, to) : Promise.resolve(null),
    [from, to]
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload}>{tr('common.refresh')}</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label={tr('banking.pnl.netSales')}     value={inr(data.revenue?.net_sales)} tone="blue" />
            <Kpi label={tr('banking.pnl.totalCosts')}   value={inr(data.costs?.total_costs)} tone="orange" />
            <Kpi label={tr('banking.pnl.grossProfit')}  value={inr(data.profit?.gross_profit)} tone={Number(data.profit?.gross_profit) >= 0 ? 'green' : 'red'} />
            <Kpi label={tr('banking.pnl.netProfit')}    value={inr(data.profit?.net_profit)}   tone={Number(data.profit?.net_profit) >= 0 ? 'green' : 'red'} />
            <Kpi label={tr('banking.pnl.netMargin')}    value={`${fmtNum(data.profit?.net_margin_pct || 0)}%`} />
          </div>

          <div className="pnl-grid">
            <div className="section-card">
              <div className="section-header" style={{ background: '#e6f9ee', color: '#1e6e3b' }}>{tr('banking.pnl.revenue')}</div>
              <PnLRow label={tr('banking.pnl.grossSales')}    value={data.revenue?.gross_sales} />
              <PnLRow label={tr('banking.pnl.salesReturns')}  value={data.revenue?.sales_returns} negative />
              <PnLRow label={tr('banking.pnl.netSales')}      value={data.revenue?.net_sales} bold />
              <PnLRow label={tr('banking.pnl.miscIncome')}    value={data.revenue?.misc_income} />
            </div>
            <div className="section-card">
              <div className="section-header" style={{ background: '#fff3e6', color: '#a0531a' }}>{tr('banking.pnl.costsExpenses')}</div>
              <PnLRow label={tr('banking.pnl.purchases')}    value={data.costs?.purchases} />
              <PnLRow label={tr('banking.pnl.itemLoss')}    value={data.costs?.item_loss} />
              <PnLRow label={tr('banking.pnl.otherExpenses')} value={data.costs?.other_exp} />
              <PnLRow label={tr('banking.pnl.totalCosts')}  value={data.costs?.total_costs} bold />
            </div>
          </div>

          <div className="section-card pnl-summary">
            <div className="section-header">{tr('banking.pnl.bottomLine')} — {from} to {to}</div>
            <PnLRow label={tr('banking.pnl.netSales')}     value={data.revenue?.net_sales} />
            <PnLRow label={'(−) ' + tr('banking.pnl.purchases')} value={data.costs?.purchases} negative />
            <PnLRow label={'(−) ' + tr('banking.pnl.itemLoss')} value={data.costs?.item_loss} negative />
            <PnLRow label={tr('banking.pnl.grossProfit')}  value={data.profit?.gross_profit} bold />
            <PnLRow label={'(+) ' + tr('banking.pnl.miscIncome')}  value={data.revenue?.misc_income} />
            <PnLRow label={'(−) ' + tr('banking.pnl.otherExpenses')} value={data.costs?.other_exp} negative />
            <PnLRow label={tr('banking.pnl.netProfitTotal')}    value={data.profit?.net_profit} bold big />
          </div>
        </>
      )}
    </div>
  );
}

function PnLRow({ label, value, bold, big, negative }) {
  const v = Number(value || 0);
  return (
    <div className={`pnl-row ${bold ? 'pnl-bold' : ''} ${big ? 'pnl-big' : ''}`}>
      <span>{label}</span>
      <span style={{ color: negative ? 'var(--warning)' : (v < 0 ? 'var(--danger)' : 'inherit') }}>{inr(v)}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   4. BALANCE SHEET
═══════════════════════════════════════════════════════════ */
function BalanceSheetTab() {
  const { t: tr } = useTranslation();
  const [asOf, setAsOf] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.financials.balanceSheet(asOf), [asOf]
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>{tr('banking.asOf')}</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload}>{tr('common.refresh')}</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label={tr('banking.bs.totalAssets')}      value={inr(data.assets?.total_assets)}       tone="green" />
            <Kpi label={tr('banking.bs.totalLiabilities')} value={inr(data.liabilities?.total_liabilities)} tone="orange" />
            <Kpi label={tr('banking.bs.ownersEquity')}    value={inr(data.equity)}                     tone="blue" />
          </div>

          <div className="pnl-grid">
            <div className="section-card">
              <div className="section-header" style={{ background: '#e6f9ee', color: '#1e6e3b' }}>{tr('banking.bs.assets')}</div>
              <PnLRow label={tr('banking.bs.cashInHand')}        value={data.assets?.cash} />
              <PnLRow label={tr('banking.bs.bankBalances')}       value={data.assets?.bank} />
              <PnLRow label={tr('banking.bs.customerReceivables')} value={data.assets?.customer_receivables} />
              <PnLRow label={tr('banking.bs.inventoryValue')}     value={data.assets?.inventory_value} />
              <PnLRow label={tr('banking.bs.totalAssetsRow')}        value={data.assets?.total_assets} bold big />
            </div>
            <div className="section-card">
              <div className="section-header" style={{ background: '#fff3e6', color: '#a0531a' }}>{tr('banking.bs.liabilitiesEquity')}</div>
              <PnLRow label={tr('banking.bs.supplierPayables')}   value={data.liabilities?.supplier_payables} />
              <PnLRow label={tr('banking.bs.totalLiabilitiesRow')}   value={data.liabilities?.total_liabilities} bold />
              <PnLRow label={tr('banking.bs.ownersEquity')}      value={data.equity} bold />
              <PnLRow label={tr('banking.bs.totalLE')}         value={Number(data.liabilities?.total_liabilities || 0) + Number(data.equity || 0)} bold big />
            </div>
          </div>

          {!data.balanced && (
            <div style={{ padding: 10, background: '#fff3e6', border: '1px solid #f6a623', borderRadius: 6, fontSize: 13 }}>
              ⚠️ {tr('banking.bs.notBalanced')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`bk-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
